import type { CharacterTimelineData, CharacterHistoryTimelineOptions, StoryTime } from '../types/timeline';
import { compareCalendarDates, defaultCalendar, formatCalendarDate, type CalendarConfig } from './calendar';
import { formatEarthTime, parseWorldClock } from './calendarConversion';

export type StoryAxisSource = Pick<CharacterTimelineData, 'chapters' | 'scenes' | 'segments'> | Pick<CharacterHistoryTimelineOptions, 'chapters' | 'scenes' | 'segments'>;
export type StoryAxisMode = 'narrative' | 'chronological';
export type StoryAxisPoint = {
  key: string;
  chapterId: string | null;
  chapter: string;
  sceneId: string;
  scene: string;
  scenePosition: number;
  segmentId: string | null;
  segment: string;
  narrativeIndex: number;
  storyTime: StoryTime | null;
};
export type StoryAxisBand = { key: string; label: string; start: number; span: number };

export function compareStoryTime(left: StoryTime | null, right: StoryTime | null, calendar: CalendarConfig = defaultCalendar) {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  if (left.absoluteTime && right.absoluteTime) return new Date(left.absoluteTime).getTime() - new Date(right.absoluteTime).getTime();
  const clock = (value: StoryTime) => parseWorldClock(calendar, value.clockLabel) ?? 0;
  return compareCalendarDates(calendar, left, right)
    || clock(left) - clock(right);
}

export function storyTimeLabel(value: StoryTime | null, calendar: CalendarConfig = defaultCalendar) {
  if (!value) return 'Zeit offen';
  const absolute = formatCalendarDate(calendar, value) || formatEarthTime(value.absoluteTime) || value.clockLabel || 'Zeit offen';
  if (value.timeSource !== 'relative' || !value.relativeOffsetMinutes) return absolute;
  const sign = value.relativeOffsetMinutes > 0 ? '+' : '';
  return `${absolute} (${sign}${value.relativeOffsetMinutes} Min. relativ)`;
}

export function buildStoryAxisPoints(source: StoryAxisSource | null, mode: StoryAxisMode = 'narrative', calendar: CalendarConfig = defaultCalendar): StoryAxisPoint[] {
  if (!source) return [];
  const chapters = new Map(source.chapters.map((chapter) => [chapter.id, chapter]));
  const scenes = [...source.scenes].sort((left, right) => mode === 'chronological'
    ? compareStoryTime(left.storyTime, right.storyTime, calendar) || left.narrativeIndex - right.narrativeIndex
    : left.narrativeIndex - right.narrativeIndex);
  const segmentsByScene = new Map<string, typeof source.segments>();
  for (const segment of source.segments) segmentsByScene.set(segment.sceneId, [...(segmentsByScene.get(segment.sceneId) ?? []), segment]);
  const points = scenes.flatMap<StoryAxisPoint>((scene) => {
    const chapter = scene.chapterId ? chapters.get(scene.chapterId) : null;
    const base = {
      chapterId: scene.chapterId, chapter: chapter?.title ?? 'Ohne Kapitel', sceneId: scene.id,
      scene: scene.title, scenePosition: scene.chapterPosition
    };
    const segments = [...(segmentsByScene.get(scene.id) ?? [])].sort((left, right) => left.narrativeIndex - right.narrativeIndex);
    return [
      { ...base, key: `scene:${scene.id}`, segmentId: null, segment: 'Szenenbeginn', narrativeIndex: scene.narrativeIndex, storyTime: scene.storyTime },
      ...segments.map((segment) => ({ ...base, key: `segment:${segment.id}`, segmentId: segment.id, segment: segment.label,
        narrativeIndex: segment.narrativeIndex, storyTime: segment.storyTime }))
    ];
  });
  return points;
}

export function groupStoryAxis(points: StoryAxisPoint[], label: (point: StoryAxisPoint) => string): StoryAxisBand[] {
  const result: StoryAxisBand[] = [];
  points.forEach((point, index) => {
    const value = label(point); const previous = result[result.length - 1];
    if (previous?.label === value) previous.span += 1;
    else result.push({ key: `${index}-${value}`, label: value, start: index + 1, span: 1 });
  });
  return result;
}

export function narrativeBoundary(source: StoryAxisSource | null, sceneId: string | null, segmentId: string | null, boundary: 'start' | 'end') {
  if (!source || !sceneId) return boundary === 'start' ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  if (segmentId) return source.segments.find((segment) => segment.id === segmentId && segment.sceneId === sceneId)?.narrativeIndex;
  const scene = source.scenes.find((candidate) => candidate.id === sceneId);
  if (!scene) return undefined;
  if (boundary === 'start') return scene.narrativeIndex;
  const segments = source.segments.filter((segment) => segment.sceneId === sceneId);
  return segments.length ? Math.max(...segments.map((segment) => segment.narrativeIndex)) + 1 : scene.narrativeIndex + 1;
}
